import { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  alpha,
  Box,
  Divider,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChevronDown, Clock3, Mail, UserRound } from 'lucide-react';

import type { ScrapedEmailDetail } from '$api/MailAutomationAPI';
import type { WorkflowRunAttachmentSummary } from '$api/WorkflowRunsAPI';
import { Stack } from '$layouts/v2/components/Stack';
import { formatRelativeTime } from '$pages/mail-automation/utils';

import { WorkflowRunAttachments } from './WorkflowRunAttachments';

export type WorkflowRunEmailSnapshot = {
  subject: string;
  body: string;
  fromAddress: string;
  fromName: string;
  mailbox: string;
  receivedAt: string;
};

export type WorkflowRunEmailContext = {
  current: WorkflowRunEmailSnapshot | null;
  previous: WorkflowRunEmailSnapshot[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const stringField = (value: Record<string, unknown>, key: string) =>
  typeof value[key] === 'string' ? value[key] : '';

const readEmailSnapshot = (value: unknown): WorkflowRunEmailSnapshot | null => {
  if (!isRecord(value)) {
    return null;
  }

  const emailKeys = [
    'emailSubject',
    'emailBody',
    'emailFrom',
    'emailFromName',
    'mailbox',
    'receivedAt',
  ];
  if (!emailKeys.some((key) => key in value)) {
    return null;
  }

  return {
    subject: stringField(value, 'emailSubject'),
    body: stringField(value, 'emailBody'),
    fromAddress: stringField(value, 'emailFrom'),
    fromName: stringField(value, 'emailFromName'),
    mailbox: stringField(value, 'mailbox'),
    receivedAt: stringField(value, 'receivedAt'),
  };
};

const isSameEmail = (
  left: WorkflowRunEmailSnapshot,
  right: WorkflowRunEmailSnapshot,
) =>
  Boolean(left.receivedAt) &&
  left.receivedAt === right.receivedAt &&
  left.fromAddress === right.fromAddress &&
  left.subject === right.subject;

export const readWorkflowRunEmailContext = (
  data: Record<string, unknown>,
): WorkflowRunEmailContext => {
  const currentFromData = readEmailSnapshot(data);
  const conversation = Array.isArray(data.emailConversation)
    ? data.emailConversation
        .map((item) => readEmailSnapshot(item))
        .filter((item): item is WorkflowRunEmailSnapshot => item !== null)
    : [];

  if (!currentFromData) {
    return {
      current: conversation.at(-1) ?? null,
      previous: conversation.slice(0, -1),
    };
  }

  let currentIndex = -1;
  for (let index = conversation.length - 1; index >= 0; index -= 1) {
    if (isSameEmail(currentFromData, conversation[index])) {
      currentIndex = index;
      break;
    }
  }

  if (currentIndex === -1) {
    return { current: currentFromData, previous: conversation };
  }

  const capturedCurrent = conversation[currentIndex];
  return {
    current: {
      ...capturedCurrent,
      ...currentFromData,
      body: currentFromData.body || capturedCurrent.body,
      fromName: currentFromData.fromName || capturedCurrent.fromName,
    },
    previous: conversation.slice(0, currentIndex),
  };
};

const formatDateTime = (value: string) => {
  const parsed = new Date(value);
  if (!value || Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(parsed);
};

function EmailBody({
  body,
  maxHeight = 280,
}: {
  body: string;
  maxHeight?: number;
}) {
  return (
    <Box
      sx={{
        p: 1.5,
        maxHeight,
        overflowY: 'auto',
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        fontSize: 13,
        lineHeight: 1.55,
        color: 'text.primary',
        bgcolor: 'background.default',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      {body || 'No cleaned email body was captured.'}
    </Box>
  );
}

function PreviousEmail({
  email,
  index,
}: {
  email: WorkflowRunEmailSnapshot;
  index: number;
}) {
  const sender = email.fromName || email.fromAddress || 'Unknown sender';

  return (
    <Accordion
      disableGutters
      elevation={0}
      square
      sx={{
        bgcolor: 'transparent',
        border: 0,
        borderTop: 1,
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 1, borderColor: 'divider' },
      }}
    >
      <AccordionSummary
        expandIcon={<ChevronDown size={15} />}
        aria-controls={`previous-email-${index}-content`}
        id={`previous-email-${index}-header`}
        sx={{
          minHeight: 54,
          px: 0.5,
          '&.Mui-expanded': { minHeight: 54 },
          '& .MuiAccordionSummary-content': {
            my: 1,
            minWidth: 0,
          },
          '& .MuiAccordionSummary-content.Mui-expanded': { my: 1 },
        }}
      >
        <Box sx={{ minWidth: 0, pr: 1 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: 600, lineHeight: 1.35 }}
          >
            {email.subject || '(No subject)'}
          </Typography>
          <Typography
            variant="caption"
            noWrap
            sx={{ display: 'block', lineHeight: 1.4 }}
          >
            {sender}
            {email.receivedAt
              ? ` · ${formatRelativeTime(email.receivedAt)}`
              : ''}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails
        id={`previous-email-${index}-content`}
        sx={{ px: 0.5, pt: 0, pb: 1.5 }}
      >
        <Stack spacing={1}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 0.25, sm: 1 }}
            justifyContent="space-between"
          >
            <Typography variant="caption">
              {email.fromName && email.fromAddress
                ? `${email.fromName} <${email.fromAddress}>`
                : sender}
            </Typography>
            {email.receivedAt && (
              <Typography variant="caption">
                {formatDateTime(email.receivedAt)}
              </Typography>
            )}
          </Stack>
          <EmailBody body={email.body} />
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

export function WorkflowRunEmailContext({
  context,
  email,
  isEmailLoading,
  emailError,
  fallbackSubject,
  fallbackMailbox,
  attachments,
  token,
  workflowRunId,
}: {
  context: WorkflowRunEmailContext;
  email: ScrapedEmailDetail | null;
  isEmailLoading: boolean;
  emailError: string | null;
  fallbackSubject: string;
  fallbackMailbox: string;
  attachments: WorkflowRunAttachmentSummary;
  token?: string | null;
  workflowRunId?: string;
}) {
  const [bodyTab, setBodyTab] = useState(0);

  useEffect(() => {
    setBodyTab(0);
  }, [email?.id]);

  const current = context.current;
  const subject = email?.subject || current?.subject || fallbackSubject;
  const fromName = email?.from.emailAddress.name || current?.fromName || '';
  const fromAddress =
    email?.from.emailAddress.address || current?.fromAddress || '';
  const sender = fromName || fromAddress || 'Unknown sender';
  const mailbox =
    email?.mailbox.mailAddress || current?.mailbox || fallbackMailbox;
  const receivedAt = email?.receivedDateTime || current?.receivedAt || '';
  const cleanedBody = email?.cleanedBody || current?.body || '';
  const originalBody = email?.originalBody || '';
  const previousEmails = context.previous.toReversed();

  useEffect(() => {
    if (!originalBody && bodyTab === 1) {
      setBodyTab(0);
    }
  }, [bodyTab, originalBody]);

  return (
    <Paper
      variant="outlined"
      sx={{
        minWidth: 0,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          sx={{
            minHeight: 58,
            px: 2,
            position: 'sticky',
            top: 0,
            zIndex: 2,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              display: 'grid',
              placeItems: 'center',
              borderRadius: 1.25,
              color: 'secondary.main',
              bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.12),
            }}
          >
            <Mail size={16} />
          </Box>
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography
              id="workflow-run-email-context-title"
              variant="subtitle2"
            >
              Email context
            </Typography>
            <Typography variant="caption">
              {context.previous.length}{' '}
              {context.previous.length === 1
                ? 'previous email'
                : 'previous emails'}
            </Typography>
          </Box>
        </Stack>

        <Box
          sx={{
            flexGrow: 1,
            minHeight: 0,
            p: { xs: 1.5, sm: 2.25 },
            overflowY: 'auto',
          }}
        >
          {!email && !current && isEmailLoading && (
            <Stack spacing={1.25}>
              <Skeleton variant="text" width="24%" />
              <Skeleton variant="text" height={32} />
              <Skeleton variant="text" width="55%" />
              <Skeleton variant="rounded" height={240} />
            </Stack>
          )}
          {!email && !current && attachments.total === 0 && !isEmailLoading && (
            <Alert severity="warning">
              No email context was captured for this workflow run.
            </Alert>
          )}
          {Boolean(email || current || attachments.total > 0) && (
            <>
              <Typography
                variant="overline"
                sx={{
                  color: 'secondary.main',
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.8,
                  lineHeight: 1.5,
                }}
              >
                Current email
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mt: 0.25,
                  mb: 1,
                  fontSize: 16,
                  lineHeight: 1.35,
                  overflowWrap: 'anywhere',
                }}
              >
                {subject || '(No subject)'}
              </Typography>

              <Stack spacing={0.6} sx={{ mb: 1.5 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <UserRound size={13} />
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
                    {fromName && fromAddress
                      ? `${fromName} <${fromAddress}>`
                      : sender}
                  </Typography>
                </Stack>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 0.5, sm: 1.5 }}
                >
                  {receivedAt && (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Clock3 size={13} />
                      <Tooltip title={formatDateTime(receivedAt)}>
                        <Typography variant="caption">
                          {formatRelativeTime(receivedAt)}
                        </Typography>
                      </Tooltip>
                    </Stack>
                  )}
                  {mailbox && (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Mail size={13} />
                      <Typography variant="caption">{mailbox}</Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>

              <WorkflowRunAttachments
                summary={attachments}
                token={token}
                workflowRunId={workflowRunId}
              />

              {emailError && (
                <Alert severity="info" sx={{ mb: 1.5 }}>
                  Scraped email details are unavailable. Showing the email
                  context captured by this run.
                </Alert>
              )}

              <Divider sx={{ my: 2.25 }} />

              <Stack
                direction="row"
                alignItems="baseline"
                justifyContent="space-between"
                sx={{ mb: 0.75 }}
              >
                <Typography variant="subtitle2">
                  Earlier in this conversation
                </Typography>
                <Typography variant="caption">
                  {context.previous.length}
                  {context.previous.length > 1 ? ' · newest first' : ''}
                </Typography>
              </Stack>

              {context.previous.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    py: 2,
                    px: 1.5,
                    textAlign: 'center',
                    border: 1,
                    borderStyle: 'dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  This email started the conversation.
                </Typography>
              ) : (
                previousEmails.map((previousEmail, index) => (
                  <PreviousEmail
                    key={`${previousEmail.receivedAt}-${previousEmail.fromAddress}-${index}`}
                    email={previousEmail}
                    index={index}
                  />
                ))
              )}

              <Divider sx={{ my: 2.25 }} />

              <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
                Current email content
              </Typography>
              <Tabs
                value={bodyTab}
                onChange={(_event, value: number) => setBodyTab(value)}
                sx={{
                  mb: 1.25,
                  minHeight: 34,
                  borderBottom: 1,
                  borderColor: 'divider',
                  '& .MuiTab-root': {
                    minHeight: 34,
                    px: 1.25,
                    py: 0.5,
                    fontSize: 12,
                  },
                }}
              >
                <Tab label="Cleaned text" />
                <Tab label="Original HTML" disabled={!originalBody} />
              </Tabs>

              {bodyTab === 0 ? (
                <EmailBody body={cleanedBody} maxHeight={320} />
              ) : (
                <Box
                  sx={{
                    overflow: 'hidden',
                    bgcolor: 'common.white',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Box
                    component="iframe"
                    srcDoc={originalBody}
                    sandbox=""
                    title="Original email body"
                    sx={{
                      width: '100%',
                      height: 320,
                      display: 'block',
                      border: 0,
                      bgcolor: 'common.white',
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
